// R1b-δ — Audit & Trust tabbed shell. Owner + Administrator gate.
// Financial is owner-only (money = owner). Compliance Reports export is owner-only.
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ListTree, LinkIcon, Search, FileDown } from "lucide-react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import AuditLedgerTab from "./tabs/AuditLedgerTab";
import AuditChainVerificationTab from "./tabs/AuditChainVerificationTab";
import AuditProofSearchTab from "./tabs/AuditProofSearchTab";
import AuditComplianceReportsTab from "./tabs/AuditComplianceReportsTab";

const ALL_TABS = [
  { key: "ledger",              label: "Ledger",              icon: ListTree, Body: AuditLedgerTab,             testid: "audit-tab-ledger",              adminOk: true },
  { key: "chain-verification",  label: "Chain Verification",  icon: LinkIcon, Body: AuditChainVerificationTab,  testid: "audit-tab-chain-verification",  adminOk: true },
  { key: "proof-search",        label: "Proof Search",        icon: Search,   Body: AuditProofSearchTab,        testid: "audit-tab-proof-search",        adminOk: true },
  { key: "compliance-reports",  label: "Compliance Reports",  icon: FileDown, Body: AuditComplianceReportsTab,  testid: "audit-tab-compliance-reports",  adminOk: false },
];

export default function AuditTrustShell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tab: tabParam } = useParams();
  const isOwner = user?.role_key === "OWNER_SUPER_ADMIN";
  const visibleTabs = ALL_TABS.filter((t) => isOwner || t.adminOk);
  const current = visibleTabs.find((t) => t.key === tabParam) || visibleTabs[0];
  // If admin tried to open compliance-reports directly, current will be ledger.
  const requestedDeniedTab = tabParam && !visibleTabs.find((t) => t.key === tabParam);
  const CurrentBody = current.Body;

  return (
    <AdminAppShell
      activeNav="audit-trust"
      title="Audit & Trust"
      subtitle={visibleTabs.map((t) => t.label).join(" · ")}
      breadcrumbs={[
        { label: "Owner Command Center", to: "/owner" },
        { label: "Audit & Trust", to: "/admin/audit-trust/ledger" },
        { label: current.label, to: null },
      ]}
      user={user}
    >
      {requestedDeniedTab && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-xs font-nav" data-testid="audit-admin-denied-tab-notice">
          Compliance Reports export is Owner-only. Showing Ledger instead.
        </div>
      )}
      <div
        role="tablist"
        aria-label="Audit & Trust sections"
        data-testid="audit-tablist"
        style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "1px solid #E8E4D9", overflowX: "auto" }}
      >
        {visibleTabs.map((t) => {
          const Icon = t.icon;
          const isActive = t.key === current.key;
          return (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={isActive}
              aria-controls={`audit-panel-${t.key}`}
              tabIndex={isActive ? 0 : -1}
              data-testid={t.testid}
              onClick={() => navigate(`/admin/audit-trust/${t.key}`)}
              onKeyDown={(e) => {
                if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                  e.preventDefault();
                  const dir = e.key === "ArrowRight" ? 1 : -1;
                  const idx = visibleTabs.findIndex((x) => x.key === current.key);
                  const next = visibleTabs[(idx + dir + visibleTabs.length) % visibleTabs.length];
                  navigate(`/admin/audit-trust/${next.key}`);
                }
              }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "10px 16px", minHeight: 44,
                background: isActive ? "#FFFFFF" : "transparent",
                border: 0, borderBottom: isActive ? "2px solid #B8892B" : "2px solid transparent",
                marginBottom: "-1px",
                fontFamily: '"Inter Tight", system-ui, sans-serif',
                fontSize: 13, fontWeight: isActive ? 600 : 500,
                color: isActive ? "#0F0F10" : "#4A4A4C",
                cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              <Icon size={14} aria-hidden="true" /> {t.label}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" id={`audit-panel-${current.key}`} data-testid={`audit-panel-${current.key}`} aria-live="polite">
        <CurrentBody isOwner={isOwner} />
      </div>
    </AdminAppShell>
  );
}
